import {
    useRecurly,
    CardNumberElement,
    CardMonthElement,
    CardYearElement,
    CardCvvElement,
} from '@recurly/react-recurly';
import { createLogger } from 'logging';
import React, { JSX, useState, useRef } from 'react';
import { BillingInfo } from 'subscription-service-client';
import { twMerge } from 'tailwind-merge';

import { Button } from '@videoblocks/design-system';

import { updateBillingInfo } from './actions';
import BillingInfoCountryInput from './billingInfoCountryInput';
import BillingInfoTextInput from './billingInfoTextInput';

import './recurlyStyles.css';

const logger = createLogger('CreditCardInfoForm');

export default function CreditCardInfoForm({ billingInfo }: { billingInfo?: BillingInfo }): JSX.Element {
    const recurly = useRecurly();
    const formRef = useRef<HTMLFormElement>(null);
    const [error, setError] = useState<string | null>(null);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isEditable, setIsEditable] = useState<boolean>(false);
    const submitText = 'Save Changes';

    const cancelEdit = () => {
        setIsEditable(false);
        setError(null);
        setSuccessMessage(null);
    };

    const openEditing = (e: React.FormEvent) => {
        e.preventDefault();
        setIsEditable(true);
        setError(null);
        setSuccessMessage(null);
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);
        setSuccessMessage(null);
        setIsLoading(true);

        if (!formRef.current) {
            setError('Form reference not available');
            setIsLoading(false);
            return;
        }

        recurly.token(formRef.current, (err, token) => {
            if (err) {
                console.error(err);
                setError(err.message);
                setIsLoading(false);
            } else {
                updateBillingInfo({ tokenId: token.id }).then(
                    (result) => {
                        setIsLoading(false);
                        if (result.success) {
                            setIsEditable(false);
                            setSuccessMessage('Update successful');
                        } else {
                            setError(result.error || '');
                        }
                    },
                    (error) => {
                        setIsLoading(false);
                        setError(
                            'An error occurred while updating your billing information. Please try again later. If the issue persists, please contact support.',
                        );
                        logger.error(error);
                    },
                );
            }
        });
    };

    return (
        <form ref={formRef} onSubmit={isEditable ? handleSubmit : openEditing} className="flex flex-col w-full">
            <div className="flex flex-row justify-between items-center">
                <h2 className="text-2xl font-semibold my-6">Payment Information</h2>
                <div>
                    {/* -------- Cancel -------- */}
                    {isEditable && (
                        <button
                            onClick={() => cancelEdit()}
                            role="button"
                            type="reset"
                            className="w-36 border-none bg-none text-red-400 font-semibold hover:underline"
                        >
                            Cancel
                        </button>
                    )}
                    {/* -------- Submit -------- */}
                    <Button className="h-min rounded-full" type="submit" role="submit" disabled={isLoading}>
                        {isLoading ? 'Saving...' : isEditable ? submitText : 'Update Payment Info'}
                    </Button>
                </div>
            </div>

            {error && <div style={{ color: 'red' }}>{error}</div>}
            {successMessage && <div style={{ color: 'green' }}>{successMessage}</div>}

            <BillingInfoTextInput
                label="First Name"
                placeholder={billingInfo?.firstName ?? ''}
                name="firstName"
                recurlyId="first_name"
                isEditable={isEditable}
            />
            <BillingInfoTextInput
                label="Last Name"
                placeholder={billingInfo?.lastName ?? ''}
                name="lastName"
                recurlyId="last_name"
                isEditable={isEditable}
            />
            <div className="flex flex-row justify-between items-center py-4 border-b-1 border-gray-300">
                <label className="w-full">Credit Card Number</label>
                {isEditable ? (
                    <CardNumberElement
                        className={twMerge('w-full', !isEditable && 'font-bold')}
                        style={{
                            placeholder: {
                                content: `XXXX-XXXX-XXXX-${billingInfo?.lastFour ?? 'XXXX'}`,
                                color: '#99a1af',
                            },
                        }}
                    />
                ) : (
                    <span className="w-full py-2 text-gray-800 font-bold">
                        XXXX-XXXX-XXXX-{billingInfo?.lastFour ?? 'XXXX'}
                    </span>
                )}
            </div>
            <div className="flex flex-row justify-between items-center py-4 border-b-1 border-gray-300">
                <label className="w-full">Expiration Date</label>
                <div className={twMerge('flex flex-row items-center justify-start w-full', isEditable && 'space-x-4')}>
                    {isEditable ? (
                        <CardMonthElement
                            style={{
                                placeholder: {
                                    content: billingInfo?.expMonth?.toString(),
                                    color: '#99a1af',
                                },
                                padding: '0px',
                            }}
                        />
                    ) : (
                        <span className="py-2 text-gray-800 font-bold">{billingInfo?.expMonth?.toString()}</span>
                    )}
                    <span className="font-bold">/</span>
                    {isEditable ? (
                        <CardYearElement
                            style={{
                                placeholder: {
                                    content: billingInfo?.expYear?.toString(),
                                    color: '#99a1af',
                                },
                                padding: '0px',
                            }}
                        />
                    ) : (
                        <span className="py-2 text-gray-800 font-bold">{billingInfo?.expYear?.toString()}</span>
                    )}
                </div>
            </div>
            <div className="flex flex-row justify-between items-center py-4 border-b-1 border-gray-300">
                <label className="w-full">Security Code/CVV</label>
                {isEditable ? (
                    <CardCvvElement
                        className="w-full"
                        style={{
                            placeholder: {
                                content: '●●●',
                                color: '#99a1af',
                            },
                        }}
                    />
                ) : (
                    <span className="w-full py-2 text-gray-800 font-bold">●●●</span>
                )}
            </div>
            <BillingInfoTextInput
                label="Zip/Postal Code"
                placeholder={billingInfo?.postalCode ?? ''}
                name="postalCode"
                recurlyId="postal_code"
                isEditable={isEditable}
            />
            <BillingInfoCountryInput initialCountry={billingInfo?.country ?? ''} isEditable={isEditable} />
        </form>
    );
}
